import { useMemo, useState } from 'react';
import { IconArrowsExchange2, IconCopy, IconSwitchHorizontal } from '@tabler/icons-react';
import { bijoyToUnicode, unicodeToBijoy } from '@abdalgolabs/ansi-unicode-converter';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';

const DIRECTIONS = ['bijoyToUnicode', 'unicodeToBijoy'];

const textareaClass = 'min-h-[260px] w-full resize-y rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] leading-7 text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';

function directionButtonClass(active) {
    return `flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-[13px] font-semibold transition-colors ${
        active
            ? 'border-sky-500 bg-sky-50 text-sky-700 dark:border-sky-500 dark:bg-sky-900/30 dark:text-sky-300'
            : 'border-[#ECE8E4] text-[#6B635C] hover:border-sky-400/50 dark:border-zinc-700 dark:text-zinc-300'
    }`;
}

export default function BijoyUnicodeText() {
    const { t } = useTranslation();

    const [direction, setDirection] = useState(DIRECTIONS[0]);
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    const inputLabel = direction === 'bijoyToUnicode'
        ? t('bijoyUnicodeText.bijoyLabel')
        : t('bijoyUnicodeText.unicodeLabel');
    const outputLabel = direction === 'bijoyToUnicode'
        ? t('bijoyUnicodeText.unicodeLabel')
        : t('bijoyUnicodeText.bijoyLabel');
    const hasInput = inputText.trim().length > 0;
    const canConvert = hasInput && !processing;

    const handleConvert = () => {
        if (!hasInput || processing) return;

        recordToolUsage('text/bijoy-unicode', { category: 'text' });

        setProcessing(true);
        setError(null);

        try {
            const converted = direction === 'bijoyToUnicode'
                ? bijoyToUnicode(inputText)
                : unicodeToBijoy(inputText);
            setOutputText(converted);
            notifications.show({
                title: t('common.success'),
                message: t('bijoyUnicodeText.done'),
                color: 'green',
            });
        } catch {
            const msg = t('bijoyUnicodeText.errors.failed');
            setOutputText('');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const handleCopy = () => {
        if (!outputText) return;

        if (copyTextToClipboard(outputText)) {
            notifications.show({
                title: t('common.success'),
                message: t('bijoyUnicodeText.copied'),
                color: 'green',
            });
            return;
        }

        void navigator.clipboard?.writeText(outputText)
            .then(() => {
                notifications.show({
                    title: t('common.success'),
                    message: t('bijoyUnicodeText.copied'),
                    color: 'green',
                });
            })
            .catch(() => {
                notifications.show({
                    title: t('common.error'),
                    message: t('bijoyUnicodeText.copyFailed'),
                    color: 'red',
                });
            });
    };

    const handleReset = () => {
        setInputText('');
        setOutputText('');
        setError(null);
    };

    const handleDirectionChange = (nextDirection) => {
        if (nextDirection === direction) return;

        setDirection(nextDirection);
        setInputText(outputText || inputText);
        setOutputText('');
        setError(null);
    };

    const status = deriveToolStatus({
        hasInput,
        canRun: canConvert,
        processing,
        hasResult: outputText.length > 0,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`bijoyUnicodeText.howTo.step${i}`)),
        [t],
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('bijoyUnicodeText.statusReady'),
        success: t('bijoyUnicodeText.done'),
        error: error || t('toolPage.statusText.error'),
    });

    return (
        <ToolPageLayout
            slug="text/bijoy-unicode"
            subtitleKey="bijoyUnicodeText.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('bijoyUnicodeText.inputTitle')}
            howToSteps={howToSteps}
            onReset={inputText || outputText || error ? handleReset : undefined}
            primaryAction={{
                label: direction === 'bijoyToUnicode'
                    ? t('bijoyUnicodeText.convertToUnicode')
                    : t('bijoyUnicodeText.convertToBijoy'),
                onClick: handleConvert,
                disabled: !canConvert,
                loading: processing,
                icon: <IconArrowsExchange2 size={18} stroke={1.9} />,
            }}
            controls={(
                <ToolCard title={t('bijoyUnicodeText.directionTitle')}>
                    <div className="grid grid-cols-1 gap-2">
                        {DIRECTIONS.map((item) => (
                            <button
                                key={item}
                                type="button"
                                onClick={() => handleDirectionChange(item)}
                                className={directionButtonClass(direction === item)}
                            >
                                <IconSwitchHorizontal size={16} stroke={1.9} />
                                {t(`bijoyUnicodeText.directions.${item}`)}
                            </button>
                        ))}
                    </div>
                </ToolCard>
            )}
            settings={(
                <ToolCard title={inputLabel}>
                    <textarea
                        value={inputText}
                        onChange={(e) => {
                            setInputText(e.target.value);
                            setOutputText('');
                            setError(null);
                        }}
                        placeholder={direction === 'bijoyToUnicode'
                            ? t('bijoyUnicodeText.bijoyPlaceholder')
                            : t('bijoyUnicodeText.unicodePlaceholder')}
                        className={textareaClass}
                    />
                    <p className="mt-2 text-right text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                        {t('bijoyUnicodeText.characterCount', { count: toBnDigits(inputText.length) })}
                    </p>
                </ToolCard>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('bijoyUnicodeText.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('bijoyUnicodeText.previewEmpty')}
                    idleHint={t('bijoyUnicodeText.previewEmptyHint')}
                    processingLabel={t('bijoyUnicodeText.processing')}
                    error={error}
                    successLabel={t('bijoyUnicodeText.done')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('bijoyUnicodeText.convert')} /> : null}
                    showIdle={!hasInput && !error}
                >
                    <div className="flex h-full min-h-0 flex-col gap-2.5">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {outputLabel}
                            </span>
                            <button
                                type="button"
                                onClick={handleCopy}
                                disabled={!outputText}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-sky-400/50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconCopy size={14} stroke={1.9} />
                                {t('common.copy')}
                            </button>
                        </div>
                        <textarea
                            readOnly
                            value={outputText}
                            placeholder={t('bijoyUnicodeText.outputPlaceholder')}
                            className="h-full min-h-0 w-full resize-none rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] leading-7 text-[#3D3833] outline-none placeholder:text-[#A8A099] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500"
                        />
                        <p className="text-right text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('bijoyUnicodeText.characterCount', { count: toBnDigits(outputText.length) })}
                        </p>
                    </div>
                </ToolPreviewPanel>
            )}
        />
    );
}
